import type { ExcelDownloadClient } from "app/components/React/ExcelButton/ExcelClientDownload";
import type { LeadsType } from "app/pages/sales-intelligence/modules/leads-search";
import {
    BASE_ENDPOINT_PATH,
    BASE_ADV_SEARCH_ENDPOINT_PATH,
} from "app/pages/sales-intelligence/sub-modules/advanced-search/services/api/const";
import { getApiPathForProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import type { IFetchService, PostConfigObject } from "app/services/fetchService";

import type { Account, ContactSearchEntry, CRMEntity } from "../../types/common";
import type {
    ContactRevealByIdRequestDto,
    ContactsExportRequestDto,
    ContactsResponseDto,
    ContactsSearchResponseWithTotalCountDto,
    ContactsSearchResultsRequestDto,
    ExportContactsToCrmRequestDto,
    ExportContactsToCrmResponseDto,
    FetchExportToCrmPreviewResponseDto,
    RevealedContactDto,
    RevealedContactsResponseDto,
    TopNRevealRequestDto,
} from "../../types/dto";

const COMPANIES_BASE_ENDPOINT_PATH = `${BASE_ENDPOINT_PATH}/companies/contacts`;
const DOMAINS_BASE_ENDPOINT_PATH = `${BASE_ENDPOINT_PATH}/contacts`;

const COMPANIES_CONTACTS_BASE_ENDPOINT_PATH = `${BASE_ADV_SEARCH_ENDPOINT_PATH}/companies/contacts/search`;
const WEBSITES_CONTACTS_BASE_ENDPOINT_PATH = `${BASE_ADV_SEARCH_ENDPOINT_PATH}/websites/contacts/search`;

export default function createContactsSearchApiService(
    fetchService: IFetchService,
    excelDownloadClient: ExcelDownloadClient,
) {
    const getBaseEndpointPath = (leadsType: LeadsType): string => {
        if (leadsType === "companies") {
            return COMPANIES_BASE_ENDPOINT_PATH;
        }

        return DOMAINS_BASE_ENDPOINT_PATH;
    };

    const getContactsEndpointPath = (leadsType: LeadsType): string => {
        if (leadsType === "companies") {
            return COMPANIES_CONTACTS_BASE_ENDPOINT_PATH;
        }

        return WEBSITES_CONTACTS_BASE_ENDPOINT_PATH;
    };

    return {
        fetchSearchResults(
            leadsType: LeadsType,
            dto: ContactsSearchResultsRequestDto,
            config?: PostConfigObject,
        ): Promise<ContactsResponseDto> {
            return fetchService.post(getContactsEndpointPath(leadsType), dto, config);
        },
        fetchContactsSearchResultsWithTotalCount(
            dto: ContactsSearchResultsRequestDto,
            config?: PostConfigObject,
        ): Promise<ContactsSearchResponseWithTotalCountDto> {
            return fetchService.post(`${DOMAINS_BASE_ENDPOINT_PATH}/search`, dto, config);
        },

        revealContactsById(dto: readonly ContactRevealByIdRequestDto[]): Promise<RevealedContactsResponseDto> {
            return fetchService.post(`${DOMAINS_BASE_ENDPOINT_PATH}/reveal/identity`, dto);
        },
        revealStatus(requestId: string): Promise<number> {
            return fetchService.get(`${DOMAINS_BASE_ENDPOINT_PATH}/reveal/status`, { requestId });
        },
        revealSingleContact(dto: ContactRevealByIdRequestDto): Promise<RevealedContactDto> {
            return fetchService.post(`${DOMAINS_BASE_ENDPOINT_PATH}/reveal/single`, dto);
        },
        revealTopNContacts(dto: TopNRevealRequestDto, leadsType: LeadsType): Promise<RevealedContactsResponseDto> {
            return fetchService.post(`${getBaseEndpointPath(leadsType)}/reveal`, dto);
        },

        exportToExcel(dto: ContactsExportRequestDto, leadsType: LeadsType) {
            return excelDownloadClient(`${getBaseEndpointPath(leadsType)}/excel`, dto);
        },
        exportContactsToCrm(payload: ExportContactsToCrmRequestDto, integrationName: Integration, as: CRMEntity) {
            const apiPath = getApiPathForProvider(integrationName);
            return fetchService.post<ExportContactsToCrmResponseDto>(
                `/sales-api/${apiPath}/export/companies/${as}`,
                payload,
            );
        },

        fetchExportToCrmPreview(contacts: ContactSearchEntry[], crm: Integration, options?: PostConfigObject) {
            const apiPath = getApiPathForProvider(crm);
            const contactsDto = contacts.map((c) => ({ ...c, prospect: c.site, contactId: c.id }));

            return fetchService.post<FetchExportToCrmPreviewResponseDto>(
                `/sales-api/${apiPath}/export/contacts/preview`,
                { contacts: contactsDto },
                options,
            );
        },

        searchAccounts(
            searchText: string,
            integrationName: Integration,
            perPage: number,
            page: number,
            cancellation: AbortSignal,
        ) {
            const params = { searchText, limit: perPage, after: page * perPage };
            const apiPath = getApiPathForProvider(integrationName);
            return fetchService
                .get<{ data: Account[] }>(`/sales-api/${apiPath}/search-accounts`, params, {
                    cancellation,
                })
                .then((res) => res.data);
        },
    };
}
